import React, { useEffect, useRef } from 'react';
import { NavLink, To, useLocation } from 'react-router-dom';
import { cx } from './clases';

export interface PestanaNav {
  id: string;
  titulo: string;
  /** Título en teléfono («Pendientes» en vez de «Pendientes de catálogo»). */
  tituloCorto?: string;
  to: To;
  /** Número junto al título. `null`/`undefined` = sin contador (todavía no hay dato). */
  contador?: number | null;
  /** `aviso` (ámbar) para lo que bloquea, `info` (azul) para lo que espera. */
  tonoContador?: 'info' | 'aviso';
}

interface Props {
  pestanas: PestanaNav[];
  etiqueta?: string;
  className?: string;
}

/**
 * Pestañas del módulo como NAVEGACIÓN (`<nav>` de enlaces), no como `tablist`:
 * cada pestaña es una ruta (`/metas/captura`…) que se puede compartir, y la
 * activa lleva `aria-current="page"` (lo pone `NavLink`).
 *
 * El nombre accesible es el título completo con el contador entre paréntesis
 * («Pendientes de catálogo (6)»): así lo lee el lector de pantalla aunque en
 * el teléfono se vea el título corto.
 *
 * Si no caben (a 390 px las 5 del administrador miden 452 de 334), la barra se
 * desplaza sola hasta dejar la activa centrada: en «Accesos» no se veía en qué
 * pestaña se estaba (auditoría del 22-oct-2026). Sólo se mueve la barra
 * (`scrollLeft`), no la página.
 */
export const BarraPestanas: React.FC<Props> = ({ pestanas, etiqueta = 'Secciones del módulo', className }) => {
  const barra = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  useEffect(() => {
    const nav = barra.current;
    const activa = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!nav || !activa || nav.scrollWidth <= nav.clientWidth) return;
    nav.scrollLeft = Math.max(0, activa.offsetLeft - (nav.clientWidth - activa.offsetWidth) / 2);
  }, [pathname]);
  return (
  <nav
    ref={barra}
    aria-label={etiqueta}
    // `relative`: el `offsetLeft` de cada pestaña se mide desde la barra.
    className={cx('relative flex gap-5 overflow-x-auto border-b border-metas-borde md:gap-8', className)}
  >
    {pestanas.map((p) => {
      const hayContador = p.contador !== undefined && p.contador !== null;
      return (
        <NavLink
          key={p.id}
          to={p.to}
          aria-label={hayContador ? `${p.titulo} (${p.contador})` : p.titulo}
          data-pestana={p.id}
          className={({ isActive }) =>
            cx(
              'metas-foco metas-pestana -mb-px flex h-11 shrink-0 items-center gap-2 whitespace-nowrap border-b-2 text-[13px] font-semibold no-underline md:h-[46px] md:text-sm',
              isActive
                ? 'border-metas-primario text-metas-texto'
                : 'border-transparent text-metas-texto-3 hover:text-metas-texto',
            )
          }
        >
          {p.tituloCorto ? (
            <>
              <span className="md:hidden">{p.tituloCorto}</span>
              <span className="max-md:hidden">{p.titulo}</span>
            </>
          ) : (
            p.titulo
          )}
          {hayContador && (
            <span
              className={cx(
                'num inline-flex h-5 min-w-[20px] items-center justify-center rounded-[10px] px-1.5 text-xs',
                p.tonoContador === 'aviso' ? 'bg-metas-aviso-fondo text-metas-aviso' : 'bg-metas-info-fondo text-metas-info',
              )}
            >
              {p.contador}
            </span>
          )}
        </NavLink>
      );
    })}
  </nav>
  );
};

export default BarraPestanas;
